import React, { useState } from "react";
import { Trophy } from "lucide-react";
import "./UserDetailPopup.css";
import { addFriendById, removeFriendById } from "../utils/authService";

function UserDetailPopup({ user, monthLabel, profileDefault, onClose, lang, onFollow, currentUser, refreshLeaderboard }) {
  const [isAddingFriend, setIsAddingFriend] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  
  // Check if user is already a friend
  const isFriend = Boolean(user?.isFriend);
  
  // Check if this is the current user (can't follow/unfollow yourself)
  const isCurrentUser = currentUser && user?.id && currentUser.id === user.id;
  
  if (!user) return null;

  // Extract month name from monthLabel (e.g., "November competition" -> "November")
  const getMonthName = (label) => {
    if (!label) return "";
    // Remove " competition" or "competitie" and any trailing whitespace
    const cleaned = label
      .replace(/\s*competition\s*$/i, "")
      .replace(/\s*competitie\s*$/i, "")
      .trim();
    return cleaned;
  };

  const monthName = getMonthName(monthLabel);
  
  // Get the played count (playedMonthOdd or playedMonthEven)
  const playedCount = user?.playedCount ?? 0;
  
  const playedText = lang === "nl" 
    ? `${playedCount} keer gespeeld in ${monthName}.`
    : `Played ${playedCount} ${playedCount === 1 ? 'time' : 'times'} in ${monthName}.`;

  // Get play status (playedToday, playedFuture)
  const playedToday = user?.playedToday ?? false;
  const playedFuture = user?.playedFuture ?? false;
  
  // Get lastScore if available (only show if not undefined/null, but show if 0)
  const lastScore = user?.lastScore;
  const showScore = lastScore !== undefined && lastScore !== null;
  
  // Determine status text and color
  let statusText = "";
  let statusColor = "#000"; // default black
  
  if (playedToday) {
    const score = showScore ? lastScore : 0;
    const klikText = score === 1 ? "klik" : "kliks";
    const clickText = score === 1 ? "click" : "clicks";
    statusText = lang === "nl" 
      ? `${user?.name || "Deze speler"} scoorde vandaag ${score} ${klikText}.`
      : `${user?.name || "This player"} scored ${score} ${clickText} today.`;
    statusColor = "var(--sf-green, #22c55e)"; // green
  } else if (playedFuture) {
    statusText = lang === "nl"
      ? `${user?.name || "Deze speler"} heeft morgen al gespeeld (vanwege tijdsverschil).`
      : `${user?.name || "This player"} has played tomorrow already (due to time difference).`;
    statusColor = "var(--sf-yellow, #fee245)"; // SF-yellow
  } else {
    statusText = lang === "nl"
      ? `${user?.name || "Deze speler"} heeft vandaag nog niet gespeeld.`
      : `${user?.name || "This player"} has not played today yet.`;
    statusColor = "#000"; // black
  }

  const avatarSrc = user?.avatarUrl || profileDefault || "";

  return (
    <>
      <div className="user-detail-popup-overlay" onClick={(e) => {
        // Only handle clicks directly on the overlay (not on modal content)
        if (e.target !== e.currentTarget) {
          return;
        }
        
        // Get all overlays in the document
        const allOverlays = Array.from(document.querySelectorAll('[class*="-overlay"]'));
        
        // Find the overlay with the highest z-index (excluding overlays with pointer-events: none)
        let topmostOverlay = null;
        let topmostZIndex = -1;
        allOverlays.forEach(overlay => {
          const styles = window.getComputedStyle(overlay);
          const zIndex = parseInt(styles.zIndex, 10) || 0;
          const pointerEvents = styles.pointerEvents;
          
          // Skip overlays with pointer-events: none (like confetti)
          if (pointerEvents === 'none') {
            return;
          }
          
          if (zIndex > topmostZIndex) {
            topmostZIndex = zIndex;
            topmostOverlay = overlay;
          }
        });
        
        // If this is the topmost overlay, close it
        // If this is NOT the topmost, close the topmost instead
        if (topmostOverlay === e.currentTarget) {
          e.stopPropagation();
          onClose();
        } else if (topmostOverlay) {
          // This is not the topmost, so close the topmost modal
          e.stopPropagation();
          // Find the modal component that owns the topmost overlay and close it
          // We'll trigger a click on the topmost overlay to close it
          topmostOverlay.click();
        }
      }} />
      <div 
        className="user-detail-popup" 
        role="dialog" 
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="user-detail-popup-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        
        <img
          src={avatarSrc}
          alt=""
          className="user-detail-popup-avatar"
        />
        
        <div className="user-detail-popup-name">
          {user?.name || "Player"}
          {user?.oppositeMonthRank && Number(user?.oppositeMonthScore) > 0 && (
            <span className="user-detail-popup-trophy" title={lang === "nl" ? `Top ${user.oppositeMonthRank} vorige maand` : `Top ${user.oppositeMonthRank} last month`}>
              <Trophy size={24} strokeWidth={2} />
              <span className="user-detail-popup-trophy-rank">{user.oppositeMonthRank}</span>
            </span>
          )}
        </div>
        
        {user?.oppositeMonthRank && Number(user?.oppositeMonthScore) > 0 && (() => {
          const rank = user.oppositeMonthRank;
          let rankText = "";
          if (lang === "nl") {
            if (rank === 1) rankText = "1ste";
            else if (rank === 2) rankText = "2de";
            else if (rank === 3) rankText = "3de";
            else rankText = `${rank}e`;
          } else {
            if (rank === 1) rankText = "1st";
            else if (rank === 2) rankText = "2nd";
            else if (rank === 3) rankText = "3rd";
            else rankText = `${rank}th`;
          }
          return (
            <div className="user-detail-popup-opposite-month">
              {lang === "nl" 
                ? `${rankText} in de vorige maandcompetitie`
                : `${rankText} in last month's competition`}
            </div>
          );
        })()}
        
        <div className="user-detail-popup-played">
          {playedText}
        </div>
        
        <div className="user-detail-popup-status" style={{ color: statusColor }}>
          {statusText}
        </div>
        
        <div className="user-detail-popup-legend">
          <div className="user-detail-popup-legend-item">
            <span className="user-detail-popup-legend-dot" style={{ backgroundColor: "var(--sf-green, #22c55e)" }}></span>
            <span>{lang === "nl" ? "Vandaag gespeeld" : "Played today"}</span>
          </div>
          <div className="user-detail-popup-legend-item">
            <span className="user-detail-popup-legend-dot" style={{ backgroundColor: "#000" }}></span>
            <span>{lang === "nl" ? "Nog niet gespeeld" : "Not played yet"}</span>
          </div>
          <div className="user-detail-popup-legend-item">
            <span className="user-detail-popup-legend-dot" style={{ backgroundColor: "var(--sf-yellow, #fee245)" }}></span>
            <span>{lang === "nl" ? "Morgen al gespeeld (tijdsverschil)" : "Played tomorrow already (time difference)"}</span>
          </div>
        </div>
        
        {currentUser && user?.id && !isCurrentUser && (
          <button
            type="button"
            className="user-detail-popup-follow-button"
            onClick={async () => {
              if (isAddingFriend) return;
              
              setIsAddingFriend(true);
              setErrorMessage("");
              setSuccessMessage("");
              
              try {
                let result;
                if (isFriend) {
                  // Remove friend
                  result = await removeFriendById(user.id, lang);
                  // Use result.username if available, otherwise fallback to user.name
                  const displayName = result.username && result.username !== user.id 
                    ? result.username 
                    : (user?.name || result.username || user.id);
                  setSuccessMessage(
                    lang === "nl" 
                      ? `Je volgt ${displayName} niet meer.`
                      : `You are no longer following ${displayName}.`
                  );
                } else {
                  // Add friend
                  result = await addFriendById(user.id, lang);
                  // Use result.username if available, otherwise fallback to user.name
                  const displayName = result.username && result.username !== user.id 
                    ? result.username 
                    : (user?.name || result.username || user.id);
                  setSuccessMessage(
                    lang === "nl" 
                      ? `Je volgt nu ${displayName}.`
                      : `You are now following ${displayName}.`
                  );
                }
                
                // Refresh leaderboard if callback is provided
                if (refreshLeaderboard) {
                  refreshLeaderboard();
                }
                
                // Close popup after 2 seconds
                setTimeout(() => {
                  setSuccessMessage("");
                  onClose();
                }, 2000);
              } catch (error) {
                setErrorMessage(
                  error.message || 
                  (lang === "nl" 
                    ? (isFriend 
                        ? "Kon gebruiker niet verwijderen. Probeer het opnieuw." 
                        : "Kon gebruiker niet toevoegen. Probeer het opnieuw.")
                    : (isFriend 
                        ? "Could not remove user. Please try again." 
                        : "Could not add user. Please try again."))
                );
              } finally {
                setIsAddingFriend(false);
              }
            }}
            disabled={isAddingFriend}
          >
            {isAddingFriend 
              ? (lang === "nl" 
                  ? (isFriend ? "Verwijderen..." : "Toevoegen...")
                  : (isFriend ? "Removing..." : "Adding..."))
              : (lang === "nl" 
                  ? (isFriend ? "niet meer volgen" : "+ volg")
                  : (isFriend ? "unfollow" : "+ follow"))
            }
          </button>
        )}
        
        {errorMessage && (
          <div className="user-detail-popup-error">{errorMessage}</div>
        )}
        
        {successMessage && (
          <div className="user-detail-popup-success">{successMessage}</div>
        )}
      </div>
    </>
  );
}

export default UserDetailPopup;
